/*
 * Copyright © 2025 Gavin Sawyer. All rights reserved.
 */

import { DOCUMENT, isPlatformBrowser }                                           from "@angular/common";
import { HttpClient }                                                            from "@angular/common/http";
import { inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 }          from "@angular/core";
import { doc, type DocumentReference, type DocumentSnapshot, Firestore, getDoc } from "@angular/fire/firestore";
import type * as brandLib                                                        from "@bowstring/brand";
import type * as configLib                                                       from "@bowstring/config";
import { BRAND, CONFIG }                                                         from "@bowstring/injection-tokens";
import { type AccountDocument }                                                  from "@bowstring/interfaces";
import { take }                                                                  from "rxjs";
import { AuthenticationService }                                                 from "./AuthenticationService";


@Injectable({ providedIn: "root" })
export class SpotifyPlayerLoaderService {

  private readonly authenticationService: AuthenticationService = inject<AuthenticationService>(AuthenticationService);
  private readonly brandLib: typeof brandLib                    = inject<typeof brandLib>(BRAND);
  private readonly configLib: typeof configLib                  = inject<typeof configLib>(CONFIG);
  private readonly document: Document                           = inject<Document>(DOCUMENT);
  private readonly firestore: Firestore                         = inject<Firestore>(Firestore);
  private readonly httpClient: HttpClient                       = inject<HttpClient>(HttpClient);
  private readonly platformId: NonNullable<unknown>             = inject<NonNullable<unknown>>(PLATFORM_ID);
  private readonly rendererFactory2: RendererFactory2           = inject<RendererFactory2>(RendererFactory2);

  private spotifyPlayer?: Spotify.Player;
  private spotifyPlayerPromise?: Promise<Spotify.Player>;
  private renderer2?: Renderer2;

  public async load(): Promise<Spotify.Player | null> {
    if (!isPlatformBrowser(this.platformId))
      return null;

    return this.spotifyPlayer || this.spotifyPlayerPromise || ((): Promise<Spotify.Player> => {
      this.spotifyPlayerPromise = new Promise<Spotify.Player>(
        (
          resolve: (value: Spotify.Player) => void,
          reject: (reason?: unknown) => void,
        ): void => {
          this.httpClient.get(
            "https://sdk.scdn.co/spotify-player.js",
            { responseType: "blob" },
          ).pipe<Blob>(
            take<Blob>(1),
          ).subscribe(
            {
              error: (error: Error): void => reject(error),
              next:  (spotifyWebPlaybackSdkJsBlob: Blob): void => {
                const spotifyWebPlaybackSdkJsSrc: string = URL.createObjectURL(spotifyWebPlaybackSdkJsBlob);

                if (!this.document.defaultView)
                  return void (0);

                this.document.defaultView.onSpotifyWebPlaybackSDKReady = (): void => {
                  URL.revokeObjectURL(spotifyWebPlaybackSdkJsSrc);

                  const spotifyPlayerClass: typeof Spotify.Player | undefined = this.document.defaultView ? this.document.defaultView.Spotify.Player : undefined;

                  if (spotifyPlayerClass) {
                    const spotifyPlayer: Spotify.Player = new spotifyPlayerClass(
                      {
                        getOAuthToken: (callback: (token: string) => void): void => {
                          const userId: string | undefined = this.authenticationService.user$()?.uid;

                          if (!userId)
                            throw new Error("You're not signed in.");

                          getDoc(
                            doc(
                              this.firestore,
                              `/accounts/${ userId }`,
                            ) as DocumentReference<AccountDocument, AccountDocument>,
                          ).then<void, never>(
                            (accountDocumentSnapshot: DocumentSnapshot<AccountDocument, AccountDocument>): Promise<void> => {
                              const refreshToken: string | undefined = accountDocumentSnapshot.data()?.connectedApps?.spotify?.refreshToken;

                              if (!refreshToken)
                                throw new Error("A value for `connectedApps.spotify.refreshToken` is missing from the account document.");

                              return fetch(
                                "https://accounts.spotify.com/api/token",
                                {
                                  body:    new URLSearchParams(
                                    {
                                      client_id:     this.configLib.spotifyClientId,
                                      grant_type:    "refresh_token",
                                      refresh_token: refreshToken,
                                    },
                                  ),
                                  headers: { "Content-Type": "application/x-www-form-urlencoded" },
                                  method:  "POST",
                                },
                              ).then<void, never>(
                                (response: Response): Promise<void> => response.json().then<void>(
                                  ({ accessToken }: { "accessToken": string }): void => callback(accessToken),
                                ),
                                (error: Error): never => {
                                  console.error(error);

                                  throw new Error("Something went wrong.");
                                },
                              );
                            },
                            (error: Error): never => {
                              console.error(error);

                              throw new Error("Something went wrong.");
                            },
                          );
                        },
                        name:          `${ this.brandLib.title } Player`,
                      },
                    );

                    spotifyPlayer.connect().then<void, never>(
                      (successful: boolean): void => successful ? resolve(spotifyPlayer) : reject(),
                      (error: Error): never => {
                        reject(error);

                        throw error;
                      },
                    );
                  } else
                    reject("Something went wrong.");
                };

                this.renderer2 = this.rendererFactory2.createRenderer(
                  this.document.head,
                  null,
                );

                const htmlScriptElement: HTMLScriptElement = this.renderer2.createElement("script");

                this.renderer2.setAttribute(
                  htmlScriptElement,
                  "src",
                  spotifyWebPlaybackSdkJsSrc,
                );
                this.renderer2.setAttribute(
                  htmlScriptElement,
                  "type",
                  "text/javascript",
                );
                this.renderer2.appendChild(
                  this.document.head,
                  htmlScriptElement,
                );
              },
            },
          );
        },
      );

      return this.spotifyPlayerPromise;
    })();
  }

}
